/**
 * errorReporter.ts
 *
 * Pure TypeScript module for capturing, buffering, and flushing frontend error
 * reports to the backend observability API. Zero React dependencies.
 *
 * Design principles:
 * - Fire-and-forget: flush failures are silently swallowed
 * - Re-entrant guard: captureError during an active flush is a no-op
 * - Uses raw fetch() (not api() helper) to avoid circular error loops
 * - SSR-safe: all window/navigator access is guarded
 *
 * Observability surfaces:
 * - getBufferSize() — programmatic buffer inspection
 * - Network tab: POST calls to /api/_obs/errors/batch visible in browser DevTools
 * - _frontend_errors table in obs DB for stored errors
 */

import { API_BASE } from './api';

export interface ErrorContext {
  componentStack?: string;
  projectId?: string;
}

interface ErrorReport {
  projectId?: string | null;
  errorMessage: string;
  stackTrace?: string | null;
  componentStack?: string | null;
  url?: string | null;
  userId?: string | null;
  userAgent?: string | null;
  metadata?: Record<string, unknown> | null;
  occurredAt: string; // ISO 8601
}

const MAX_BUFFER_SIZE = 50;
const FLUSH_THRESHOLD = 5;
const FLUSH_INTERVAL_MS = 10_000;
const BATCH_ENDPOINT = `${API_BASE}/api/_obs/errors/batch`;

let buffer: ErrorReport[] = [];
let isFlushing = false;
let flushTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Extracts the projectId from the current URL.
 * Supports /projects/, /preview/, and /app/ path segments with optional locale prefix.
 */
function extractProjectId(): string | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(
    /^(?:\/[a-z]{2})?\/(?:projects|preview|app)\/([^/]+)/
  );
  return match?.[1] ?? null;
}

/**
 * Capture an error and add it to the in-memory buffer.
 * If the buffer has reached FLUSH_THRESHOLD, automatically triggers a flush.
 * No-op if a flush is currently in progress (re-entrant guard).
 */
export function captureError(error: unknown, context?: ErrorContext): void {
  if (isFlushing) return;

  const report: ErrorReport = {
    errorMessage: error instanceof Error ? error.message : String(error),
    stackTrace: error instanceof Error ? (error.stack ?? null) : null,
    componentStack: context?.componentStack ?? null,
    url: typeof window !== 'undefined' ? window.location.href : null,
    userId: null,
    userAgent: typeof window !== 'undefined' ? navigator.userAgent : null,
    projectId: context?.projectId ?? extractProjectId(),
    occurredAt: new Date().toISOString(),
  };

  if (buffer.length >= MAX_BUFFER_SIZE) {
    buffer.shift(); // Drop oldest entry to make room
  }
  buffer.push(report);

  if (buffer.length >= FLUSH_THRESHOLD) {
    flushErrors();
  }
}

/**
 * Flush all buffered errors to the backend API.
 * Fire-and-forget: network failures are silently swallowed.
 * No-op if already flushing or buffer is empty.
 */
export async function flushErrors(): Promise<void> {
  if (buffer.length === 0 || isFlushing) return;

  isFlushing = true;
  const batch = buffer.splice(0);

  try {
    await fetch(BATCH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(batch),
      keepalive: true,
    });
  } catch {
    // Fire-and-forget: silently swallow network errors
  } finally {
    isFlushing = false;
  }
}

/**
 * Synchronous flush using sendBeacon — for use in beforeunload handlers.
 * sendBeacon allows the request to complete even after page unload.
 * No-op if buffer is empty.
 */
export function flushErrorsSync(): void {
  if (buffer.length === 0) return;

  const json = JSON.stringify(buffer);
  const blob = new Blob([json], { type: 'application/json' });
  navigator.sendBeacon(BATCH_ENDPOINT, blob);
  buffer = [];
}

/**
 * Start the automatic periodic flush timer.
 * Idempotent — calling multiple times is safe.
 */
export function startAutoFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setInterval(() => {
    flushErrors();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Stop the automatic periodic flush timer.
 * Idempotent — calling when not running is safe.
 */
export function stopAutoFlush(): void {
  if (flushTimer === null) return;
  clearInterval(flushTimer);
  flushTimer = null;
}

/**
 * Returns the current number of buffered (unflushed) error reports.
 * Exported for programmatic buffer inspection / diagnostics.
 */
export function getBufferSize(): number {
  return buffer.length;
}

/**
 * Reset all module state. Intended for test cleanup only.
 * Clears buffer, resets isFlushing flag, stops auto-flush timer.
 */
export function resetBuffer(): void {
  buffer = [];
  isFlushing = false;
  stopAutoFlush();
}
